"use client";

import Link from "next/link";
import { useCallback, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { boardEntryStillCurrentAt } from "@/lib/conditions/board-freshness";
import {
  HUB_BOARD_ROWS_DESKTOP,
  HUB_BOARD_ROWS_PHONE,
  HUB_SPORT_GROUPS,
  hubSport,
  hubSportsInGroup,
  isHubSportKey,
  normaliseHubSportParam,
} from "@/lib/conditions/hub/sports";
import type {
  HubAllBoard,
  HubBoard as HubBoardData,
  HubBoardRow,
  HubNote,
  HubSport,
  HubSportGroup,
  HubSportKey,
} from "@/lib/conditions/hub/types";
import { useBrowserMinuteClock } from "@/lib/hooks/use-minute-clock";
import { useShallowSearchParam } from "@/lib/hooks/use-shallow-search-param";
import { HubArrowIcon, HubChevronDownIcon, HubChevronIcon, HubIcon, type HubGlyph } from "./hub-icons";
import motion from "./hub-motion.module.css";
import { HubBandPill, HubSafetyPill, HubScoreRing, HUB_SAFETY_LABEL } from "./hub-parts";
import { HUB_SPORT_SHEET_ID, HubSportSheet } from "./HubSportSheet";
import { HUB_BOARD_ID, HUB_SPORT_PARAM, selectHubSport, type HubSportSelectSource } from "./hub-sport-select";

/**
 * The F4 sport picker and leaderboard card (client island). Desktop: "All sports" plus grouped
 * sport pins (no scores). Phone: a full-width "Sport: All" toggle that opens `HubSportSheet`.
 * The card shows the chosen sport's top 10 (desktop) or top 5 (phone), or the All board in the
 * mode the page passes. The choice is shareable as `?sport=<key>` (shallow, canonical unchanged).
 *
 * Every row is a reading judged current at render; the rows are judged again on the browser's
 * minute clock after hydration (the `ScoreCurrentList` rule: the first client render equals the
 * server's, then a reading past its location's local midnight is withdrawn and the rest re-rank).
 */

export { HUB_BOARD_ID, HUB_SPORT_PARAM, selectHubSport };

export type HubBoardProps = Readonly<{
  /** Shown sports, picker order (`HubPageData.sports`). */
  sports: readonly HubSport[];
  /** Board per shown sport (`HubPageData.boards`). */
  boards: Readonly<Partial<Record<HubSportKey, HubBoardData>>>;
  /** The All board (`HubPageData.allBoard`). */
  allBoard: HubAllBoard;
  namedPublic: boolean;
  /** "{hotCount} spots at 70+ right now" (phone). */
  hotCount: number;
  /** Desktop: the right-hand column beside the card (season awards, legend). Phone: below it. */
  aside?: ReactNode;
}>;

/** Picker rows on desktop: Snow, Bike, Waves; then Wind, Water. */
const PICKER_ROWS: readonly (readonly HubSportGroup[])[] = [["snow", "bike", "waves"], ["wind", "water"]];

const GROUP_LABEL = new Map(HUB_SPORT_GROUPS.map((group) => [group.key, group.label]));

/**
 * The rows still current on the browser clock, and whether any was withdrawn. When one was, the
 * counts judged at render ("12 spots ranked", the CTA count, "70+ right now") are no longer
 * trusted and are hidden rather than shown over fewer rows.
 */
function useCurrentRows(rows: readonly HubBoardRow[]): Readonly<{ rows: readonly HubBoardRow[]; withdrawn: boolean }> {
  const nowMs = useBrowserMinuteClock();
  if (nowMs === null) return { rows, withdrawn: false };
  const current = rows.filter((row) => boardEntryStillCurrentAt(row.freshness, nowMs));
  return { rows: current, withdrawn: current.length < rows.length };
}

/** A CTA label without its judged-at-render count suffix (" (267 spots)"). */
function withoutCount(label: string): string {
  return label.replace(/ \([^)]*\)$/, "");
}

const WITHDRAWN_META = "Ranked by today's GO score · refreshed every three hours";

function SportPin({
  sport,
  on,
  off,
  onPick,
}: Readonly<{ sport: HubSport; on: boolean; off: boolean; onPick: () => void }>) {
  const aria = `${sport.label} leaderboard${off ? ", off-season" : ""}${sport.beta ? ", beta" : ""}`;
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-controls={HUB_BOARD_ID}
      aria-label={aria}
      onClick={onPick}
      data-hub-pin={sport.key}
      className={`flex min-h-[76px] w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-[12px] border px-1.5 pb-2 pt-[9px] text-center font-[inherit] text-[13.5px] font-semibold leading-[1.2] transition-[background-color,color,border-color,box-shadow] duration-200 hover:border-navy ${
        on ? "border-navy bg-navy text-white shadow-[0_12px_26px_rgba(22,35,59,0.22)]" : "border-border bg-white text-navy"
      }`}
    >
      <span className={`flex ${on ? "text-teal-light" : "text-teal-dark"}`}>
        <HubIcon name={sport.icon} size={24} />
      </span>
      <span className="[overflow-wrap:break-word] [hyphens:auto]">{sport.label}</span>
      {off || sport.beta ? (
        <span className="flex items-center justify-center gap-[5px]">
          {off ? (
            <span className={`rounded-full px-[7px] py-px text-[11px] font-semibold ${on ? "bg-white/12 text-[#E5E9EF]" : "bg-[#EDEFF2] text-[#454C5A]"}`}>
              Off-season
            </span>
          ) : null}
          {sport.beta ? (
            <span
              className={`rounded-full border px-1.5 py-px text-[10px] font-bold uppercase tracking-[0.04em] ${on ? "border-white/40 text-white" : "border-[#C9D3D6] text-[#566072]"}`}
            >
              Beta
            </span>
          ) : null}
        </span>
      ) : null}
    </button>
  );
}

function NoteLink({ note }: Readonly<{ note: HubNote }>) {
  if (!note.link) return null;
  return (
    <>
      {" "}
      <Link href={note.link.href} className="font-semibold text-teal-dark underline hover:text-teal">
        {note.link.label}
      </Link>
    </>
  );
}

function BoardNote({ desktop, phone }: Readonly<{ desktop: HubNote; phone: HubNote }>) {
  return (
    <>
      <div className="mx-3 mb-1 mt-3 rounded-[12px] border border-border bg-warm-bg px-3 py-2.5 md:hidden" data-hub-note="phone">
        <p className="m-0 text-[12px] font-bold uppercase tracking-[0.08em] text-teal-dark">{phone.title}</p>
        <p className="m-0 mt-[3px] text-[13px] leading-[1.42] text-text-body">
          {phone.text}
          <NoteLink note={phone} />
        </p>
      </div>
      <div className="mx-5 mb-1.5 mt-4 hidden min-h-16 rounded-[12px] border border-border bg-warm-bg px-4 py-[11px] md:block" data-hub-note="desktop">
        <p className="m-0 text-[14px] leading-[1.45] text-text-body">
          <span className="font-bold text-teal-dark">{desktop.title}.</span> {desktop.text}
          <NoteLink note={desktop} />
        </p>
      </div>
    </>
  );
}

function rowAria(row: HubBoardRow, rank: number, isAll: boolean): string {
  const parts: string[] = [`${rank}. ${row.name}, ${row.country}`];
  if (isAll) {
    const lower = isHubSportKey(row.sportKey) ? hubSport(row.sportKey).lowerName : row.sportLabel;
    parts.push(`best for ${lower}`);
  }
  parts.push(`GO score ${row.score100} out of 100`, row.band);
  if (row.safety) parts.push(`safety: ${HUB_SAFETY_LABEL[row.safety]}`);
  return parts.join(", ");
}

function BoardRow({
  row,
  rank,
  isAll,
  detailColumn,
}: Readonly<{ row: HubBoardRow; rank: number; isAll: boolean; detailColumn: boolean }>) {
  const index = rank - 1;
  const stagger = isAll ? 45 : 70;
  const sportIcon: HubGlyph = isHubSportKey(row.sportKey) ? hubSport(row.sportKey).icon : "all";
  const grid = detailColumn ? GRID_WITH_DETAIL : GRID_PLAIN;
  return (
    <li
      className={`${motion.row} border-b border-[#EDF1EC] last:border-b-0 ${index >= HUB_BOARD_ROWS_PHONE ? "max-md:hidden" : ""}`}
      style={index > 0 ? { animationDelay: `${index * stagger}ms` } : undefined}
      data-hub-row={row.slug}
    >
      <Link
        href={row.href}
        aria-label={rowAria(row, rank, isAll)}
        className={`${motion.go} flex items-center gap-2.5 rounded-[14px] px-2.5 py-2 text-text-body no-underline transition-colors duration-200 hover:bg-[#F4F8F7] hover:text-text-body md:grid md:gap-4 md:px-4 ${grid} ${
          isAll ? "min-h-[72px] md:min-h-16" : "min-h-[84px]"
        }`}
      >
        <span aria-hidden="true" className="w-[22px] shrink-0 text-center text-[19px] font-bold text-navy md:w-auto md:text-left md:text-[22px]">
          {rank}
        </span>
        <span className="flex min-w-0 flex-auto flex-col gap-1 md:gap-[5px]">
          <span className="truncate text-[15.5px] font-bold leading-[1.2] text-navy md:text-[18px]">{row.name}</span>
          <span className="flex min-w-0 items-center gap-[7px] md:gap-2">
            <HubBandPill label={row.band} tone={row.bandTone} />
            {isAll ? (
              <span className="inline-flex min-w-0 items-center gap-1 text-[13px] font-semibold text-teal-dark lg:hidden">
                <span className="flex shrink-0">
                  <HubIcon name={sportIcon} size={16} strokeWidth={1.9} />
                </span>
                <span className="min-w-0 truncate">{row.sportLabel}</span>
              </span>
            ) : null}
            <span className={`min-w-0 truncate text-[13px] text-[#566072] md:text-[14px] ${isAll ? "hidden lg:inline" : ""}`}>
              {row.country}
            </span>
            {row.safety && !detailColumn ? (
              <span className="hidden shrink-0 md:inline-flex">
                <HubSafetyPill safety={row.safety} />
              </span>
            ) : null}
          </span>
          {isAll ? (
            <span className="flex min-w-0 items-center gap-2 lg:hidden">
              <span className="truncate text-[12.5px] text-[#566072]">{row.country}</span>
              <HubSafetyPill safety={row.safety} />
            </span>
          ) : row.safety ? (
            <span className={detailColumn ? "lg:hidden" : "md:hidden"}>
              <HubSafetyPill safety={row.safety} />
            </span>
          ) : null}
        </span>
        {detailColumn ? (
          <span className="hidden min-w-0 flex-col gap-1 text-[13px] leading-[1.35] text-[#566072] lg:flex">
            {isAll ? (
              <span className="flex items-center gap-[9px] text-[14.5px] font-semibold text-navy">
                <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-[9px] bg-[#E4F2F3] text-teal-dark">
                  <HubIcon name={sportIcon} size={19} strokeWidth={1.8} />
                </span>
                <span className="min-w-0 truncate">{row.sportLabel}</span>
              </span>
            ) : null}
            {row.safety ? (
              <span>
                <HubSafetyPill safety={row.safety} />
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="flex shrink-0 justify-center">
          <HubScoreRing
            score100={row.score100}
            className="size-12 md:size-[54px]"
            numberClassName="text-[17px] md:text-[18px]"
            animate
            delayMs={120 + index * stagger}
          />
        </span>
        <span aria-hidden="true" className={`${motion.arrow} shrink-0 text-teal-dark`}>
          <HubChevronIcon />
        </span>
      </Link>
    </li>
  );
}

/** Rows and column heads: the detail column (best sport, safety) only fits from `lg`. */
const GRID_PLAIN = "md:grid-cols-[36px_minmax(0,1fr)_60px_20px]";
const GRID_WITH_DETAIL = "md:grid-cols-[36px_minmax(0,1fr)_60px_20px] lg:grid-cols-[36px_minmax(0,1fr)_210px_60px_20px]";

const CTA_DESKTOP =
  "hidden min-h-14 items-center justify-center gap-2.5 rounded-[12px] bg-teal px-5 text-[16.5px] font-bold text-white no-underline shadow-[0_12px_26px_rgba(22,128,143,0.3)] transition-colors hover:bg-teal-dark hover:text-white md:flex";
const CTA_PHONE =
  "flex min-h-[52px] w-full cursor-pointer items-center justify-between gap-2.5 rounded-[12px] border-0 bg-teal px-4 py-2 text-left font-[inherit] text-[15.5px] font-bold leading-[1.25] text-white no-underline shadow-[0_10px_24px_rgba(22,128,143,0.3)] transition-colors hover:bg-teal-dark hover:text-white md:hidden";

export function HubBoard({ sports, boards, allBoard, namedPublic, hotCount, aside }: HubBoardProps) {
  const param = useShallowSearchParam(HUB_SPORT_PARAM);
  const normalised = normaliseHubSportParam(param, namedPublic);
  const current: HubSportKey | null = normalised && boards[normalised] ? normalised : null;
  const board = current ? boards[current] ?? null : null;
  const all = allBoard;

  const [sheetOpen, setSheetOpen] = useState(false);
  // Focus returns to whichever control opened the sheet (the toggle or the All card's CTA).
  const openerRef = useRef<HTMLElement | null>(null);
  const openSheet = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    openerRef.current = event.currentTarget;
    setSheetOpen(true);
  }, []);
  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const pick = useCallback((sport: HubSportKey | null, source: HubSportSelectSource) => {
    selectHubSport(sport, source);
  }, []);
  const pickFromSheet = useCallback((sport: HubSportKey | null) => {
    setSheetOpen(false);
    pick(sport, "sheet");
  }, [pick]);

  const sourceRows = board ? board.rows : all.rows;
  const judged = useCurrentRows(sourceRows);
  const currentRows = judged.rows.slice(0, HUB_BOARD_ROWS_DESKTOP);
  const withdrawn = judged.withdrawn;

  const shownSports = sports.filter((sport) => boards[sport.key]);
  const offSeason = shownSports.filter((sport) => boards[sport.key]?.offSeason).map((sport) => sport.key);
  const offSet = new Set(offSeason);
  const metaFor: Partial<Record<HubSportKey, string>> = {};
  for (const sport of withdrawn ? [] : shownSports) {
    const meta = boards[sport.key]?.meta;
    if (meta) metaFor[sport.key] = meta.split(" · ")[0];
  }

  const isAll = board === null;
  const currentSport = current ? hubSport(current) : null;
  const detailColumn = isAll || board.detail === "safety";
  const titleDesktop = board ? `${board.title} leaderboard` : all.title;
  const titlePhone = board ? `${board.title} leaderboard` : all.titlePhone;
  const metaDesktop = board ? (withdrawn ? WITHDRAWN_META : board.meta) : all.meta;
  const metaPhone = board ? (withdrawn ? WITHDRAWN_META : board.meta) : all.metaPhone;
  const ctaLabel = board ? (withdrawn ? withoutCount(board.ctaLabel) : board.ctaLabel) : all.ctaLabel;
  const ctaLabelPhone = board ? (withdrawn ? withoutCount(board.ctaLabelPhone) : board.ctaLabelPhone) : all.ctaLabelPhone;
  const noteDesktop = board ? board.note : all.note;
  const notePhone = board ? board.note : all.notePhone;
  const headerIcon: HubGlyph = currentSport ? currentSport.icon : "all";
  const live = currentSport ? `Showing the ${currentSport.lowerName} leaderboard` : "Showing all sports";
  const listLabel = currentSport
    ? `${currentSport.label} leaderboard, best first`
    : `${all.title}, best first`;
  const allAria = "All sports: the best spots across in-season sports";
  const toggleAria = `Sport: ${
    currentSport
      ? `${currentSport.label}${offSet.has(currentSport.key) ? ", off-season" : ""}${currentSport.beta ? ", beta" : ""}`
      : "All sports"
  }. Change sport`;

  let emptyText: string | null = null;
  if (currentRows.length === 0) {
    emptyText = board?.offSeason
      ? "No resorts are in season, so none is ranked today."
      : "No current readings to rank right now. Check back after the next refresh.";
  }

  return (
    <section aria-labelledby="hub-pick-heading" className="mx-auto w-full max-w-[1240px] pt-[22px] md:px-5 md:pt-9">
      <div className="flex items-baseline justify-between gap-2.5 px-5 md:px-0">
        <h2 id="hub-pick-heading" className="m-0 text-[13px] font-bold uppercase tracking-[0.12em] text-teal-dark">
          Choose a sport
        </h2>
        {withdrawn ? null : <span className="text-[13px] text-[#566072] lg:hidden">{hotCount} spots at 70+ right now</span>}
        <span className="hidden text-[14px] text-[#566072] lg:inline">{shownSports.length} sports in five groups</span>
      </div>

      {/* Phone and tablet: one toggle opens the sport sheet. */}
      <div className="px-4 pb-3.5 pt-2.5 md:px-0 lg:hidden">
        <button
          type="button"
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
          aria-controls={sheetOpen ? HUB_SPORT_SHEET_ID : undefined}
          aria-label={toggleAria}
          onClick={openSheet}
          data-hub-toggle=""
          className="flex h-[52px] w-full cursor-pointer items-center gap-2.5 rounded-[14px] border border-navy bg-white pl-2 pr-3.5 font-[inherit] text-navy shadow-[0_8px_20px_rgba(22,35,59,0.08)]"
        >
          <span aria-hidden="true" className="flex size-9 shrink-0 items-center justify-center rounded-[10px] bg-navy text-teal-light">
            <HubIcon name={headerIcon} size={22} />
          </span>
          <span className="flex min-w-0 flex-auto items-baseline gap-1.5 overflow-hidden whitespace-nowrap text-left">
            <span className="text-[14px] font-medium text-[#566072]">Sport:</span>
            <span className="truncate text-[16px] font-bold text-navy">{currentSport ? currentSport.label : "All"}</span>
            {currentSport?.beta ? (
              <span className="self-center rounded-full border border-[#C9D3D6] px-1.5 text-[10px] font-bold uppercase leading-4 tracking-[0.04em] text-[#566072]">
                Beta
              </span>
            ) : null}
            {currentSport && offSet.has(currentSport.key) ? (
              <span className="self-center text-[12px] font-medium text-text-muted">Off-season</span>
            ) : null}
          </span>
          <span className="shrink-0 text-[13px] font-semibold text-teal-dark">Change</span>
          <span aria-hidden="true" className="flex shrink-0 text-teal-dark">
            <HubChevronDownIcon />
          </span>
        </button>
      </div>

      {/* Desktop (from lg, where the pins fit): All plus grouped sport pins. */}
      <div className="mt-3.5 hidden grid-cols-[136px_minmax(0,1fr)] gap-3 lg:grid">
        <button
          type="button"
          aria-pressed={isAll}
          aria-controls={HUB_BOARD_ID}
          aria-label={allAria}
          onClick={() => pick(null, "pin")}
          data-hub-pin="all"
          className={`flex h-full w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-[16px] border px-2 py-3.5 text-center font-[inherit] leading-[1.2] transition-[background-color,color,border-color,box-shadow] duration-200 hover:border-navy ${
            isAll ? "border-navy bg-navy text-white shadow-[0_12px_26px_rgba(22,35,59,0.22)]" : "border-border bg-white text-navy"
          }`}
        >
          <span className={`flex ${isAll ? "text-teal-light" : "text-teal-dark"}`}>
            <HubIcon name="all" size={30} />
          </span>
          <span className="text-[17px] font-bold">All sports</span>
        </button>
        <div className="flex flex-col gap-3">
          {PICKER_ROWS.map((row) => {
            const groups = row
              .map((key) => ({ key, items: hubSportsInGroup(shownSports, key) }))
              .filter((group) => group.items.length > 0);
            if (groups.length === 0) return null;
            return (
              <div
                key={row.join("-")}
                className="grid gap-3"
                style={{ gridTemplateColumns: groups.map((group) => `${group.items.length}fr`).join(" ") }}
              >
                {groups.map((group) => (
                  <div key={group.key} role="group" aria-labelledby={`hub-group-${group.key}`} className="min-w-0 rounded-[16px] bg-cream p-2">
                    <p id={`hub-group-${group.key}`} className="m-0 px-1.5 pb-[7px] text-[11.5px] font-bold uppercase tracking-[0.12em] text-[#454C5A]">
                      {GROUP_LABEL.get(group.key) ?? group.key}
                    </p>
                    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${group.items.length}, minmax(0, 1fr))` }}>
                      {group.items.map((sport) => (
                        <SportPin
                          key={sport.key}
                          sport={sport}
                          on={current === sport.key}
                          off={offSet.has(sport.key)}
                          onPick={() => pick(sport.key, "pin")}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      <div className={`grid items-stretch gap-[18px] md:mt-[22px] md:gap-6 ${aside ? "lg:grid-cols-[minmax(0,1fr)_380px]" : ""}`}>
        <div
          id={HUB_BOARD_ID}
          role="region"
          aria-labelledby="hub-board-heading"
          data-hub-board={current ?? "all"}
          className="mx-4 flex flex-col overflow-hidden rounded-[20px] border border-border bg-white shadow-[0_18px_40px_rgba(22,35,59,0.10)] md:mx-0 md:rounded-[22px] md:shadow-[0_22px_48px_rgba(22,35,59,0.10)]"
        >
          <div className="flex items-center gap-3 bg-navy p-4 text-white md:gap-4 md:px-6 md:py-5">
            <span
              aria-hidden="true"
              className="flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-[rgba(91,192,206,0.16)] text-teal-light md:size-[52px] md:rounded-[14px] [&_svg]:size-6 md:[&_svg]:size-7"
            >
              <HubIcon name={headerIcon} size={28} />
            </span>
            <div className="min-w-0 flex-auto">
              <h2 id="hub-board-heading" className="m-0 flex items-center gap-2.5 text-[20px] font-bold leading-[1.2] text-white md:text-[26px]">
                <span className="md:hidden">{titlePhone}</span>
                <span className="hidden md:inline">{titleDesktop}</span>
                {board?.beta ? (
                  <span className="hidden rounded-full border border-white/40 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.04em] text-white md:inline-flex">
                    Beta
                  </span>
                ) : null}
              </h2>
              <p className="m-0 mt-1 text-[13px] leading-[1.4] text-[#C9D3E0] md:text-[14.5px]">
                {board?.beta ? (
                  <span className="mr-1.5 rounded-full border border-white/40 px-1.5 text-[10.5px] font-bold uppercase tracking-[0.04em] text-white md:hidden">
                    Beta
                  </span>
                ) : null}
                <span className="md:hidden">{metaPhone}</span>
                <span className="hidden md:inline">{metaDesktop}</span>
              </p>
            </div>
            <Link
              href={board ? board.ctaHref : all.ctaHref}
              aria-label={currentSport ? `Full board: the ${currentSport.lowerName} leaderboard` : "Open the live map of every sport"}
              className={`${motion.go} hidden min-h-11 items-center gap-2 whitespace-nowrap rounded-[10px] border border-white/28 px-4 text-[14px] font-semibold text-white no-underline hover:text-white md:flex`}
            >
              {board ? "Full board" : "Live map"}
              <span className={motion.arrow}>
                <HubArrowIcon />
              </span>
            </Link>
          </div>
          <p className="sr-only" aria-live="polite">{live}</p>

          <BoardNote desktop={noteDesktop} phone={notePhone} />

          <div aria-hidden="true" className="flex justify-between px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.1em] text-[#566072] md:hidden">
            <span>{isAll ? "Spot and sport" : "Rank and spot"}</span>
            <span>GO score</span>
          </div>
          <div
            aria-hidden="true"
            className={`hidden gap-4 px-7 pb-1.5 pt-2 text-[11.5px] font-bold uppercase tracking-[0.1em] text-[#566072] md:grid ${
              detailColumn ? GRID_WITH_DETAIL : GRID_PLAIN
            }`}
          >
            <span>Rank</span>
            <span>Spot and band</span>
            {detailColumn ? <span className="hidden lg:block">{isAll ? "Best sport" : "Safety"}</span> : null}
            <span className="text-center">GO</span>
            <span />
          </div>

          {emptyText ? (
            <p className="mx-4 my-3 text-[14px] leading-[1.45] text-[#566072] md:mx-7" data-hub-empty="">
              {emptyText}
            </p>
          ) : (
            <ol aria-label={listLabel} className="m-0 list-none px-1 md:px-3" key={current ?? "all"}>
              {currentRows.map((row, index) => (
                <BoardRow
                  key={`${row.sportKey}:${row.slug}`}
                  row={row}
                  rank={index + 1}
                  isAll={isAll}
                  detailColumn={detailColumn}
                />
              ))}
            </ol>
          )}

          {board?.closedNote ? (
            <p className="mx-4 mt-2.5 text-[13px] text-[#566072] md:mx-7 md:mt-3 md:text-[14px]">{board.closedNote}</p>
          ) : null}

          <div className="mt-auto px-3 py-3.5 md:px-5 md:pb-5 md:pt-[18px]">
            {board ? (
              <>
                <Link href={board.ctaHref} className={`${motion.go} ${CTA_DESKTOP}`}>
                  <span>{ctaLabel}</span>
                  <span className={motion.arrow}>
                    <HubArrowIcon />
                  </span>
                </Link>
                <Link href={board.ctaHref} className={`${motion.go} ${CTA_PHONE}`}>
                  <span>{ctaLabelPhone}</span>
                  <span aria-hidden="true" className={motion.arrow}>
                    <HubArrowIcon />
                  </span>
                </Link>
              </>
            ) : (
              <>
                <Link href={all.ctaHref} className={`${motion.go} ${CTA_DESKTOP}`}>
                  <span>{ctaLabel}</span>
                  <span className={motion.arrow}>
                    <HubArrowIcon />
                  </span>
                </Link>
                <button
                  type="button"
                  aria-haspopup="dialog"
                  aria-controls={sheetOpen ? HUB_SPORT_SHEET_ID : undefined}
                  aria-expanded={sheetOpen}
                  onClick={openSheet}
                  className={`${motion.go} ${CTA_PHONE}`}
                >
                  <span>{ctaLabelPhone}</span>
                  <span aria-hidden="true" className={motion.arrow}>
                    <HubArrowIcon />
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
        {aside ? <div className="px-4 md:self-start md:px-0">{aside}</div> : null}
      </div>

      <HubSportSheet
        open={sheetOpen}
        sports={shownSports}
        current={current}
        offSeason={offSeason}
        metaFor={metaFor}
        allAria={allAria}
        onSelect={pickFromSheet}
        onClose={closeSheet}
        returnFocusRef={openerRef}
      />
    </section>
  );
}
